import {
  mdiAccount,
  mdiCheck,
  mdiClose,
  mdiFileDocument,
  mdiFileDocumentMultiple,
  mdiHome,
  mdiPiggyBank,
} from '@mdi/js';
import Icon from '@mdi/react';
import { Button, Tooltip } from '@mollybet/ui';
import { useTheme } from '@mollybet/ui/dist/styled';
import { Currency } from 'common/classes/currency';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import { TableTitle } from 'common/components/tables';
import { countryCodeToName } from 'common/configs/countries';
import { CurrencyCodes } from 'common/configs/currency';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import { MollyWhitelabelCustomerKYCDocument } from 'common/state/molly-whitelabel/actions';
import {
  mollyWhitelabelCustomerKYCDocumentsRecipe,
  mollyWhitelabelCustomerLiveInfoRecipe,
  mollyWhitelabelCustomerNetTransfersRecipe,
  mollyWhitelabelCustomerPLRecipe,
  mollyWhitelabelCustomerRecipe,
} from 'common/state/molly-whitelabel/recipes';
import {
  defaultMollyWhitelabelCustomerKYCDocumentsState,
  defaultMollyWhitelabelCustomerLiveInfoState,
  defaultMollyWhitelabelCustomerNetTransfersState,
  defaultMollyWhitelabelCustomerPLState,
  defaultMollyWhitelabelCustomerState,
} from 'common/state/molly-whitelabel/states';
import {
  doGetMollyWhitelabelCustomer,
  doGetMollyWhitelabelCustomerKYCDocuments,
  doGetMollyWhitelabelCustomerLiveInfo,
  doGetMollyWhitelabelCustomerPL,
  doGetMollyWhitelabelCustomerNetTransfers,
} from 'common/state/molly-whitelabel/thunkActions';
import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';

interface CustomerPageUrlParams {
  username: string;
}

interface AccountInfo {
  label: string;
  value: React.ReactNode;
}

interface AccountingRow {
  currency: CurrencyCodes;
  pl?: string;
  netTransfers?: string;
}

const TitleWithButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Pre = styled.pre`
  margin: 0;
`;

const AddressLine = styled.div`
  margin: 0.25rem;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: fit-content;

  svg {
    margin-left: 0.25rem;
  }
`;

const CustomerPageContainer = styled.div`
  display: grid;
  grid-gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(30rem, 1fr));
  width: 100%;
`;

const InfoCard = styled.div`
  border-radius: 5px;
  background-color: ${({ theme }) => theme.cardLevel1};
  padding: 1rem;
  box-shadow: ${(props) => props.theme.shadows[1]};
`;

const KycDocumentsCard = styled(InfoCard)`
  grid-column: span 2;
`;

export const CustomerPage = () => {
  const theme = useTheme();
  const { username } = useParams<CustomerPageUrlParams>();
  const [customerState, customerDispatch] = useProducerWithThunks(
    mollyWhitelabelCustomerRecipe,
    defaultMollyWhitelabelCustomerState
  );
  const [kycDocumentsState, kycDocumentsDispatch] = useProducerWithThunks(
    mollyWhitelabelCustomerKYCDocumentsRecipe,
    defaultMollyWhitelabelCustomerKYCDocumentsState
  );
  const [liveInfoState, liveInfoDispatch] = useProducerWithThunks(
    mollyWhitelabelCustomerLiveInfoRecipe,
    defaultMollyWhitelabelCustomerLiveInfoState
  );
  const [plState, plDispatch] = useProducerWithThunks(
    mollyWhitelabelCustomerPLRecipe,
    defaultMollyWhitelabelCustomerPLState
  );
  const [netTransfersState, netTransfersDispatch] = useProducerWithThunks(
    mollyWhitelabelCustomerNetTransfersRecipe,
    defaultMollyWhitelabelCustomerNetTransfersState
  );

  const { customer, pending: customerPending } = customerState;
  const { documents } = kycDocumentsState;
  const { liveInfo, pending: liveInfoPending } = liveInfoState;
  const { pl } = plState;
  const { netTransfers } = netTransfersState;

  useEffect(() => {
    customerDispatch(doGetMollyWhitelabelCustomer(username));
    kycDocumentsDispatch(doGetMollyWhitelabelCustomerKYCDocuments(username));
    liveInfoDispatch(doGetMollyWhitelabelCustomerLiveInfo(username));
    plDispatch(doGetMollyWhitelabelCustomerPL(username));
    netTransfersDispatch(doGetMollyWhitelabelCustomerNetTransfers(username));
  }, [
    username,
    customerDispatch,
    kycDocumentsDispatch,
    liveInfoDispatch,
    plDispatch,
    netTransfersDispatch,
  ]);

  const accountInfo = useMemo(
    (): AccountInfo[] => [
      { label: 'Id', value: customerPending ? 'Loading...' : customer?.id },
      {
        label: 'Name',
        value: customerPending
          ? 'Loading...'
          : `${customer?.first_name} ${customer?.last_name}`,
      },
      {
        label: 'Username',
        value: customerPending ? 'Loading...' : customer?.username,
      },
      {
        label: 'Email',
        value: customerPending ? 'Loading...' : customer?.email,
      },
      {
        label: 'Address',
        value: customerPending ? (
          'Loading...'
        ) : (
          <div>
            <AddressLine>{customer?.address}</AddressLine>
            <AddressLine>{customer?.city}</AddressLine>
            <AddressLine>{customer?.region}</AddressLine>
            <AddressLine>
              {countryCodeToName[customer?.country] || '-'} ({customer?.country}
              )
            </AddressLine>
            <AddressLine>{customer?.post_code}</AddressLine>
          </div>
        ),
      },
      {
        label: 'Phone No.',
        value: customerPending ? (
          'Loading...'
        ) : (
          <Tooltip
            content={
              customer?.telephone_verified_date
                ? `Verified: ${customer.telephone_verified_date}`
                : 'Not Verified'
            }
          >
            <IconContainer>
              <div>{customer?.telephone}</div>
              <Icon
                path={customer?.telephone_verified_date ? mdiCheck : mdiClose}
                color={
                  customer?.telephone_verified_date
                    ? theme.primary
                    : theme.danger
                }
                size={0.675}
              />
            </IconContainer>
          </Tooltip>
        ),
      },
      {
        label: 'Date of Birth',
        value: customerPending
          ? 'Loading...'
          : new Date(customer?.date_of_birth).toLocaleDateString(),
      },
      {
        label: 'Preferences',
        value: customerPending ? (
          'Loading...'
        ) : (
          <Pre>{JSON.stringify(customer?.preferences, null, 4)}</Pre>
        ),
      },
      {
        label: 'Registration Date',
        value: customerPending
          ? 'Loading...'
          : new Date(customer?.registration_date).toLocaleDateString(),
      },
      {
        label: 'Status',
        value: customerPending ? 'Loading...' : customer?.status,
      },
      {
        label: 'KYC',
        value: customerPending ? (
          'Loading...'
        ) : (
          <>
            <Tooltip
              content={`KYC ${customer?.kyc_submitted ? '' : 'not '}submitted`}
            >
              <Icon
                path={mdiFileDocument}
                size={1}
                color={
                  customer?.kyc_submitted ? theme.primary : theme.background
                }
              />
            </Tooltip>
            <Tooltip
              content={`Identity ${
                customer?.kyc_status?.identity_verified ? '' : 'not '
              }verified`}
            >
              <Icon
                path={mdiAccount}
                size={1}
                color={
                  customer?.kyc_status?.identity_verified
                    ? theme.primary
                    : theme.background
                }
              />
            </Tooltip>
            <Tooltip
              content={`Address ${
                customer?.kyc_status?.address_verified ? '' : 'not '
              }verified`}
            >
              <Icon
                path={mdiHome}
                size={1}
                color={
                  customer?.kyc_status?.address_verified
                    ? theme.primary
                    : theme.background
                }
              />
            </Tooltip>
          </>
        ),
      },
      {
        label: 'Balance',
        value: liveInfoPending
          ? 'Loading...'
          : new Currency(liveInfo?.balance, customer?.currency).toElement({
              symbol: !customerPending ? 'symbol' : false,
            }),
      },
      {
        label: 'Open Stakes',
        value: liveInfoPending
          ? 'Loading...'
          : new Currency(liveInfo?.open_stakes, customer?.currency).toElement({
              symbol: !customerPending ? 'symbol' : false,
            }),
      },
    ],
    [customer, customerPending, liveInfo, liveInfoPending, theme]
  );

  const accountColumns = useMemo(
    (): ColumnProp<AccountInfo>[] => [
      { key: 'label', title: ' ' },
      { key: 'value', title: ' ' },
    ],
    []
  );

  const accounting = useMemo((): AccountingRow[] => {
    const currencies = new Set<CurrencyCodes>([
      ...(Object.keys(pl || []) as CurrencyCodes[]),
      ...(Object.keys(netTransfers || []) as CurrencyCodes[]),
    ]);
    return Array.from(currencies).map((currency) => ({
      currency,
      pl: pl?.[currency],
      netTransfers: netTransfers?.[currency],
    }));
  }, [pl, netTransfers]);

  const accountingColumns = useMemo(
    (): ColumnProp<AccountingRow>[] => [
      { key: 'currency', title: 'Currency' },
      {
        key: 'pl',
        title: 'P/L',
        valueFormatter: (row) => new Currency(row.pl, row.currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.pl, row.currency).toString({ symbol: false }),
      },
      {
        key: 'netTransfers',
        title: 'Net Trasfers',
        valueFormatter: (row) =>
          new Currency(row.netTransfers, row.currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.netTransfers, row.currency).toString({
            symbol: false,
          }),
      },
    ],
    []
  );

  const kycDocumentsColumns = useMemo(
    (): ColumnProp<MollyWhitelabelCustomerKYCDocument>[] => [
      { key: 'id', title: 'Id' },
      {
        key: 'number',
        title: 'Number',
        valueFormatter: (row) => (row.number == null ? '-' : row.number),
      },
      { key: 'description', title: 'Description' },
      {
        key: 'issue_date',
        title: 'Issue Date',
        valueFormatter: (row) => new Date(row.issue_date).toLocaleDateString(),
      },
      {
        key: 'expiry_date',
        title: 'Expiry Date',
        valueFormatter: (row) => new Date(row.expiry_date).toLocaleDateString(),
      },
      {
        key: 'proof_of_identity',
        title: 'Proof of Identity',
        valueFormatter: (row) => (
          <Icon
            path={row.proof_of_identity ? mdiCheck : mdiClose}
            color={row.proof_of_identity ? theme.primary : theme.danger}
            size={0.75}
          />
        ),
      },
      {
        key: 'proof_of_address',
        title: 'Proof of Address',
        valueFormatter: (row) => (
          <Icon
            path={row.proof_of_address ? mdiCheck : mdiClose}
            color={row.proof_of_address ? theme.primary : theme.danger}
            size={0.75}
          />
        ),
      },
      { key: 'status', title: 'Status' },
    ],
    [theme]
  );

  return (
    <CustomerPageContainer>
      <InfoCard>
        <TitleWithButtonContainer>
          <TableTitle icon={mdiAccount} title={`User: ${username}`} />
          <Button
            onClick={() =>
              (window.location.href = `${window.urls.admin}totus/contact/${customer?.id}/change/`)
            }
          >
            Admin
          </Button>
        </TitleWithButtonContainer>
        <BaseTable
          rows={accountInfo}
          columns={accountColumns}
          csvName="Account Info"
          paginate={false}
          isSortable={false}
          isLoading={customerState.pending || liveInfoState.pending}
          error={customerState.error || liveInfoState.error}
        />
      </InfoCard>
      <InfoCard>
        <TableTitle icon={mdiPiggyBank} title="Accounting" />
        <BaseTable
          rows={accounting}
          columns={accountingColumns}
          csvName="Accounting"
          paginate={false}
          isLoading={plState.pending || netTransfersState.pending}
          error={plState.error || netTransfersState.error}
        />
      </InfoCard>
      <KycDocumentsCard>
        <TableTitle icon={mdiFileDocumentMultiple} title="KYC" />
        <BaseTable
          rows={documents || []}
          columns={kycDocumentsColumns}
          csvName="KYC Documents"
          paginate={false}
          isLoading={kycDocumentsState.pending}
          error={kycDocumentsState.error}
        />
      </KycDocumentsCard>
    </CustomerPageContainer>
  );
};

export default CustomerPage;
